/**
 * PrestationDialog — Modal partagée pour les prestations/formations.
 *
 * Utilisée sur :
 *   - /plaquette-prospects (FORMATIONS AM/A1/A2 avec image)
 *   - /prestations-pro (PRESTATIONS avec icône Lucide)
 *
 * Shape du prop `prestation` (normalisé) :
 *   {
 *     code:          string,           // identifiant interne (ex: 'am', 'passA')
 *     catalogCode:   string | null,    // code catalogue backend (null → pas de devis dispo)
 *     title:         string,
 *     subtitle:      string,
 *     badge:         string,
 *     duration:      string,
 *     price:         string,           // ex: '420 €' ou '32 €/h'
 *     priceLabel?:   string,           // ex: 'à partir de'
 *     priceNote?:    string,           // ex: 'Prix public conseillé TTC'
 *     visual:        { kind: 'img', src: string } | { kind: 'icon', Icon: LucideIcon },
 *     publicCible:   string,
 *     keyPoints:     string[],
 *     legalRef?:     string,
 *     accent?:       'orange' | 'rose' | 'blue',  // couleur dominante header (défaut orange)
 *   }
 */
import React, { useState, useEffect } from 'react';
import {
  X, Send, HelpCircle, Loader2, Clock, Euro, Users, CheckCircle2,
  Shield, FileText, AlertCircle, Info, BadgeCheck,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ACCENT_CLASSES = {
  orange: {
    header: 'from-slate-900 via-slate-800 to-slate-900',
    badge: 'bg-orange-500 hover:bg-orange-600 text-slate-900',
    price: 'text-orange-400',
    section: 'text-orange-600',
    tick: 'text-orange-500',
    submit: 'bg-orange-500 hover:bg-orange-600 text-slate-900',
    highlight: 'text-orange-500',
    cta: 'bg-orange-500 hover:bg-orange-600 text-slate-900',
  },
};

export default function PrestationDialog({ prestation, onClose }) {
  const open = !!prestation;
  const [view, setView] = useState('detail'); // detail | devis | infos | success
  const [submitting, setSubmitting] = useState(false);
  const [successPayload, setSuccessPayload] = useState(null);

  const [form, setForm] = useState({
    contact_name: '',
    contact_email: '',
    company: '',
    agrement: '',
    phone: '',
    qty: 1,
    message: '',
    consent: false,
    website: '',
  });

  // Reset state à chaque nouvelle ouverture
  useEffect(() => {
    if (prestation) {
      setView('detail');
      setSuccessPayload(null);
      setSubmitting(false);
      setForm({
        contact_name: '', contact_email: '', company: '', agrement: '',
        phone: '', qty: 1, message: '', consent: false, website: '',
      });
    }
  }, [prestation?.code]);

  const handleOpenChange = (v) => { if (!v) onClose(); };
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (!prestation) return null;

  const accent = ACCENT_CLASSES[prestation.accent || 'orange'];

  // ───────────────── SUBMIT DEVIS ─────────────────
  const submitDevis = async (e) => {
    e.preventDefault();
    if (!prestation.catalogCode) {
      toast.error('Cette prestation nécessite un devis sur mesure', {
        description: 'Utilisez « Demander des infos » pour un contact personnalisé.',
      });
      return;
    }
    if (!form.consent) {
      toast.error('Merci de cocher le consentement RGPD');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/prestation-devis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prestation_code: prestation.catalogCode,
          contact_name:    form.contact_name,
          contact_email:   form.contact_email,
          company:         form.company,
          agrement:        form.agrement,
          phone:           form.phone,
          qty:             Math.max(1, parseInt(form.qty, 10) || 1),
          message:         form.message,
          website:         form.website,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur serveur');
      setSuccessPayload({
        kind: 'devis',
        docNumber: data.doc_number,
        totalNet:  data.total_net,
        emailSent: data.email_sent,
        partnerUrl: data.partner_url,
      });
      setView('success');
      toast.success('Devis créé ! Email envoyé.', {
        description: `Devis N° ${data.doc_number} — ${data.total_net} € nets`,
      });
    } catch (err) {
      toast.error('Impossible de créer le devis', { description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  // ───────────────── SUBMIT INFOS ─────────────────
  const submitInfos = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error('Merci de cocher le consentement RGPD');
      return;
    }
    setSubmitting(true);
    try {
      const subject = `[Prestations] Demande d'infos — ${prestation.title}`;
      const message = (
        `Prestation : ${prestation.title}\n` +
        `Société : ${form.company || '—'}\n` +
        `Agrément : ${form.agrement || '—'}\n` +
        `Téléphone : ${form.phone || '—'}\n\n` +
        `Message : ${form.message || '(aucun)'}\n`
      );
      const res = await fetch(`${API_URL}/api/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.contact_name,
          email: form.contact_email,
          subject,
          message,
          consent: true,
          website: form.website,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur serveur');
      setSuccessPayload({ kind: 'infos' });
      setView('success');
      toast.success('Votre demande a bien été envoyée', {
        description: 'Réponse sous 24h ouvrées.',
      });
    } catch (err) {
      toast.error("Impossible d'envoyer la demande", { description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden gap-0 bg-white text-slate-900"
        data-testid="prestation-dialog"
      >
        {/* ───── HEADER HERO ───── */}
        <div className={`relative bg-gradient-to-br ${accent.header} text-white`}>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            data-testid="prestation-dialog-close"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition z-10"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex flex-col sm:flex-row items-stretch">
            {/* Visuel : image OU icône */}
            <div className="sm:w-44 bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center p-4 h-40 sm:h-auto shrink-0">
              {prestation.visual?.kind === 'img' && (
                <img src={prestation.visual.src} alt={prestation.title} className="max-h-full max-w-full object-contain" />
              )}
              {prestation.visual?.kind === 'icon' && prestation.visual.Icon && (
                <div className="w-20 h-20 rounded-2xl bg-orange-500/15 text-orange-500 grid place-items-center">
                  <prestation.visual.Icon className="w-10 h-10" />
                </div>
              )}
            </div>
            {/* Info */}
            <div className="flex-1 p-5 sm:p-6">
              <Badge className={`${accent.badge} border-0 text-[10px] font-black uppercase tracking-widest mb-2`}>
                {prestation.badge}
              </Badge>
              <DialogTitle
                className="text-xl sm:text-2xl font-black leading-tight text-white"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                {prestation.title}
              </DialogTitle>
              <DialogDescription className="text-slate-300 text-sm mt-1">
                {prestation.subtitle}
              </DialogDescription>
              <div className="mt-3 flex items-end gap-3">
                <div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wide">
                    {prestation.priceLabel || 'à partir de'}
                  </div>
                  <div className={`text-2xl font-black ${accent.price} tabular-nums`}>{prestation.price}</div>
                </div>
                {prestation.priceNote && (
                  <div className="text-[10px] text-slate-500 pb-1">{prestation.priceNote}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ───── CORPS ───── */}
        <div className="max-h-[55vh] overflow-y-auto">
          {view === 'detail' && (
            <DetailView
              prestation={prestation}
              accent={accent}
              onPickDevis={() => setView('devis')}
              onPickInfos={() => setView('infos')}
            />
          )}
          {view === 'devis' && (
            <DevisForm
              prestation={prestation}
              accent={accent}
              form={form} update={update}
              submitting={submitting}
              onSubmit={submitDevis}
              onBack={() => setView('detail')}
            />
          )}
          {view === 'infos' && (
            <InfosForm
              prestation={prestation}
              accent={accent}
              form={form} update={update}
              submitting={submitting}
              onSubmit={submitInfos}
              onBack={() => setView('detail')}
            />
          )}
          {view === 'success' && (
            <SuccessView payload={successPayload} accent={accent} onClose={onClose} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────── DetailView
function DetailView({ prestation, accent, onPickDevis, onPickInfos }) {
  const hasCatalog = !!prestation.catalogCode;
  return (
    <>
      <div className="p-5 sm:p-6 space-y-5">
        {/* Durée + Prix */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
              <Clock className="w-3 h-3" /> Durée
            </div>
            <div className="font-bold text-sm text-slate-800">{prestation.duration}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
              <Euro className="w-3 h-3" /> {prestation.priceLabel || 'À partir de'}
            </div>
            <div className={`font-bold text-sm ${accent.highlight}`}>{prestation.price}</div>
          </div>
        </div>

        {/* Public cible */}
        {prestation.publicCible && (
          <div>
            <div className={`flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest ${accent.section} mb-2`}>
              <Users className="w-3.5 h-3.5" /> Pour qui ?
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{prestation.publicCible}</p>
          </div>
        )}

        {/* Points clés */}
        {Array.isArray(prestation.keyPoints) && prestation.keyPoints.length > 0 && (
          <div>
            <div className={`flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest ${accent.section} mb-2`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Ce qui est inclus
            </div>
            <ul className="space-y-1.5">
              {prestation.keyPoints.map((kp, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className={`w-4 h-4 ${accent.tick} shrink-0 mt-0.5`} />
                  <span>{kp}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Cas de dispense */}
        {Array.isArray(prestation.dispenses) && prestation.dispenses.length > 0 && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-emerald-700 mb-2">
              <BadgeCheck className="w-3.5 h-3.5" /> Vous êtes dispensé si…
            </div>
            <ul className="space-y-1.5">
              {prestation.dispenses.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <BadgeCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-emerald-700/80 mt-2 italic">
              Source : service-public.gouv.fr
            </p>
          </div>
        )}

        {/* Note importante */}
        {prestation.notes && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900 leading-relaxed">{prestation.notes}</p>
            </div>
          </div>
        )}

        {/* Prix officiel de référence */}
        {prestation.priceOfficialRange && (
          <div className="rounded-lg bg-slate-100 border border-slate-200 p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
              <Euro className="w-3 h-3" /> Repère tarifaire
            </div>
            <div className="text-xs text-slate-600">{prestation.priceOfficialRange}</div>
          </div>
        )}

        {/* Référence légale */}
        {prestation.legalRef && (
          <div className="rounded-lg bg-slate-100 border border-slate-200 p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
              <Shield className="w-3 h-3" /> Cadre réglementaire
            </div>
            <div className="text-xs text-slate-600 italic">{prestation.legalRef}</div>
          </div>
        )}

        {/* Note "devis sur mesure" si pas de catalog code */}
        {!hasCatalog && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800">
              <b>Devis sur mesure :</b> cette prestation nécessite un échange personnalisé. Utilisez « Demander des infos » pour que nous vous recontactions avec une proposition adaptée.
            </div>
          </div>
        )}
      </div>

      {/* FOOTER CTA */}
      <div className="border-t border-slate-200 bg-slate-50 p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-2 sticky bottom-0">
        <Button
          onClick={onPickInfos}
          variant="outline"
          size="lg"
          className="h-12 rounded-full font-bold border-slate-300 text-slate-700 hover:bg-slate-100"
          data-testid="prestation-cta-infos"
        >
          <HelpCircle className="w-4 h-4 mr-2" />
          Demander des infos
        </Button>
        {hasCatalog && (
          <Button
            onClick={onPickDevis}
            size="lg"
            className={`h-12 rounded-full font-bold shadow-md ${accent.cta}`}
            data-testid="prestation-cta-devis"
          >
            <Send className="w-4 h-4 mr-2" />
            Demander un devis
          </Button>
        )}
      </div>
    </>
  );
}

// ───────────────────────────────────── DevisForm
function DevisForm({ prestation, accent, form, update, submitting, onSubmit, onBack }) {
  return (
    <form onSubmit={onSubmit} className="p-5 sm:p-6 space-y-4" data-testid="prestation-devis-form">
      <div className={`flex items-center gap-2 text-[11px] font-black uppercase tracking-widest ${accent.section} mb-1`}>
        <Send className="w-3.5 h-3.5" /> Devis instantané — {prestation.badge}
      </div>
      <p className="text-xs text-slate-500">
        Remplissez ces infos : vous recevrez le devis par email avec un lien pour le signer électroniquement.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <FieldInput label="Votre nom *" name="contact_name" required value={form.contact_name} onChange={(v) => update('contact_name', v)} placeholder="Prénom NOM" col={1} testid="pd-name" />
        <FieldInput label="Email *" type="email" name="contact_email" required value={form.contact_email} onChange={(v) => update('contact_email', v)} placeholder="vous@auto-ecole.fr" col={1} testid="pd-email" />
        <FieldInput label="Auto-école / Société" name="company" value={form.company} onChange={(v) => update('company', v)} placeholder="Nom de votre auto-école" col={1} testid="pd-company" />
        <FieldInput label="N° agrément" name="agrement" value={form.agrement} onChange={(v) => update('agrement', v)} placeholder="E 20 123 0000" col={1} testid="pd-agrement" />
        <FieldInput label="Téléphone" type="tel" name="phone" value={form.phone} onChange={(v) => update('phone', v)} placeholder="07 00 00 00 00" col={1} testid="pd-phone" />
        <FieldInput label="Nombre d'élèves" type="number" name="qty" min="1" max="50" value={form.qty} onChange={(v) => update('qty', v)} col={1} testid="pd-qty" />
        <FieldTextarea label="Message (optionnel)" name="message" value={form.message} onChange={(v) => update('message', v)} placeholder="Dates souhaitées, besoins particuliers…" rows={3} testid="pd-message" />
      </div>

      {/* Honeypot */}
      <input type="text" name="website" value={form.website} onChange={(e) => update('website', e.target.value)} tabIndex="-1" autoComplete="off" style={{ position: 'absolute', left: '-9999px' }} />

      <div className="flex items-start gap-2">
        <Checkbox id="consent-devis-shared" checked={form.consent} onCheckedChange={(v) => update('consent', !!v)} data-testid="pd-consent" />
        <Label htmlFor="consent-devis-shared" className="text-[11px] text-slate-500 leading-snug cursor-pointer">
          J'accepte que mes informations soient utilisées pour établir ce devis et me recontacter (RGPD).
        </Label>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onBack} className="rounded-full" data-testid="pd-back">
          ← Retour
        </Button>
        <Button type="submit" disabled={submitting} className={`flex-1 h-12 rounded-full font-bold shadow-md ${accent.submit}`} data-testid="pd-submit">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          {submitting ? 'Création en cours…' : 'Recevoir mon devis'}
        </Button>
      </div>
    </form>
  );
}

// ───────────────────────────────────── InfosForm
function InfosForm({ prestation, accent, form, update, submitting, onSubmit, onBack }) {
  return (
    <form onSubmit={onSubmit} className="p-5 sm:p-6 space-y-4" data-testid="prestation-infos-form">
      <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-700 mb-1">
        <HelpCircle className="w-3.5 h-3.5" /> Demande d'infos — {prestation.badge}
      </div>
      <p className="text-xs text-slate-500">
        Posez-nous votre question sur cette prestation, nous vous répondons sous 24h ouvrées.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <FieldInput label="Votre nom *" name="contact_name" required value={form.contact_name} onChange={(v) => update('contact_name', v)} placeholder="Prénom NOM" col={1} testid="pi-name" />
        <FieldInput label="Email *" type="email" name="contact_email" required value={form.contact_email} onChange={(v) => update('contact_email', v)} placeholder="vous@auto-ecole.fr" col={1} testid="pi-email" />
        <FieldInput label="Auto-école / Société" name="company" value={form.company} onChange={(v) => update('company', v)} placeholder="Nom de votre structure" col={1} testid="pi-company" />
        <FieldInput label="Téléphone" type="tel" name="phone" value={form.phone} onChange={(v) => update('phone', v)} col={1} testid="pi-phone" />
        <FieldTextarea label="Votre question *" required name="message" value={form.message} onChange={(v) => update('message', v)} placeholder="Posez votre question (tarifs volume, dates, planning…)" rows={4} testid="pi-message" />
      </div>

      <input type="text" name="website" value={form.website} onChange={(e) => update('website', e.target.value)} tabIndex="-1" autoComplete="off" style={{ position: 'absolute', left: '-9999px' }} />

      <div className="flex items-start gap-2">
        <Checkbox id="consent-infos-shared" checked={form.consent} onCheckedChange={(v) => update('consent', !!v)} data-testid="pi-consent" />
        <Label htmlFor="consent-infos-shared" className="text-[11px] text-slate-500 leading-snug cursor-pointer">
          J'accepte que mes informations soient utilisées pour me recontacter (RGPD).
        </Label>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onBack} className="rounded-full" data-testid="pi-back">
          ← Retour
        </Button>
        <Button type="submit" disabled={submitting} className="flex-1 h-12 rounded-full font-bold bg-slate-900 hover:bg-slate-800 text-white" data-testid="pi-submit">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          {submitting ? 'Envoi en cours…' : 'Envoyer ma question'}
        </Button>
      </div>
    </form>
  );
}

// ───────────────────────────────────── SuccessView
function SuccessView({ payload, accent, onClose }) {
  const isDevis = payload?.kind === 'devis';
  return (
    <div className="p-6 sm:p-10 text-center" data-testid="prestation-success">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
        <CheckCircle2 className="w-8 h-8" />
      </div>
      <h3 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
        {isDevis ? 'Votre devis est en route !' : 'Message envoyé !'}
      </h3>
      {isDevis ? (
        <>
          <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
            Votre devis <b className={accent.highlight}>N° {payload.docNumber}</b> ({payload.totalNet} € nets) vient d'être envoyé par email avec un lien pour le signer électroniquement.
          </p>
          <p className="text-xs text-slate-500 mt-3">
            Vérifiez vos spams si vous ne le voyez pas dans les 2 minutes.
          </p>
          {payload.partnerUrl && (
            <a
              href={payload.partnerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 mt-5 px-5 h-11 rounded-full font-bold text-sm transition shadow-md ${accent.cta}`}
              data-testid="success-view-doc"
            >
              <FileText className="w-4 h-4" /> Consulter mon devis
            </a>
          )}
        </>
      ) : (
        <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
          Nous avons bien reçu votre message et vous répondrons sous <b>24h ouvrées</b> par email.
        </p>
      )}
      <div className="mt-6">
        <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="success-close">
          Fermer
        </Button>
      </div>
    </div>
  );
}

// ───────────────────────────────────── Field helpers
function FieldInput({ label, type = 'text', name, required, value, onChange, placeholder, col, min, max, testid }) {
  return (
    <div className={`col-span-2 sm:col-span-${col || 1}`}>
      <Label className="text-[11px] font-bold">{label}</Label>
      <Input
        type={type} name={name} required={required} value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} min={min} max={max}
        data-testid={testid}
      />
    </div>
  );
}

function FieldTextarea({ label, required, name, value, onChange, placeholder, rows, testid }) {
  return (
    <div className="col-span-2">
      <Label className="text-[11px] font-bold">{label}</Label>
      <Textarea
        name={name} required={required} value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} rows={rows}
        data-testid={testid}
      />
    </div>
  );
}
